import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import type { ImApi } from "../domain/api";
import {
  hasPersistedClientMessage,
  materializeMessages,
  type MaterializedMessage,
} from "../domain/messages";
import {
  formatMessageTimeDivider,
  shouldShowTimeDivider,
} from "../domain/presentation";
import type {
  Account,
  ConnectionStatus,
  ConversationDescription,
  ConversationSummary,
  PersistedMessage,
  SendDraft,
} from "../domain/types";
import { Avatar } from "./Avatar";
import { Composer, type OutgoingActivity } from "./Composer";
import { Icon } from "./Icon";
import { MessageBubble } from "./MessageBubble";

export function ChatPanel({
  account,
  api,
  conversation,
  description,
  messages,
  loading,
  connectionStatus,
  onBack,
  onSend,
  onError,
  onEdit,
  onRetract,
  onPreference,
}: {
  readonly account: Account;
  readonly api: ImApi;
  readonly conversation: ConversationSummary | null;
  readonly description: ConversationDescription | null;
  readonly messages: readonly PersistedMessage[];
  readonly loading: boolean;
  readonly connectionStatus: ConnectionStatus;
  readonly onBack: () => void;
  readonly onSend: (draft: SendDraft) => Promise<void>;
  readonly onError: (message: string) => void;
  readonly onEdit: (message: MaterializedMessage) => void;
  readonly onRetract: (message: MaterializedMessage) => void;
  readonly onPreference: (
    conversation: ConversationSummary,
    patch: Parameters<ImApi["updateConversationPreference"]>[1],
  ) => void;
}) {
  const [showDetails, setShowDetails] = useState(false);
  const [outgoing, setOutgoing] = useState<OutgoingActivity | null>(null);
  const [showLatest, setShowLatest] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);
  const visibleMessages = useMemo(() => materializeMessages(messages), [messages]);
  const systemAccount = useMemo(() => [...messages].reverse().find(
    (message) => message.senderType === "SYSTEM_ACCOUNT" && message.systemAccount !== undefined,
  )?.systemAccount, [messages]);

  useEffect(() => {
    stickToBottomRef.current = true;
    setShowLatest(false);
    window.requestAnimationFrame(() => { scrollToBottom("auto"); });
  }, [conversation?.convId]);
  useEffect(() => {
    if (stickToBottomRef.current || outgoing !== null) {
      scrollToBottom("smooth");
      setShowLatest(false);
      return;
    }
    if (visibleMessages.length > 0) setShowLatest(true);
  }, [visibleMessages.length, loading, outgoing?.clientMsgId, outgoing?.phase]);
  useEffect(() => { setShowDetails(false); }, [conversation?.convId]);

  function scrollToBottom(behavior: ScrollBehavior): void {
    const container = scrollRef.current;
    if (container === null) return;
    container.scrollTo({ top: container.scrollHeight, behavior });
    stickToBottomRef.current = true;
  }

  function trackScrollPosition(): void {
    const container = scrollRef.current;
    if (container === null) return;
    const atBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 96;
    stickToBottomRef.current = atBottom;
    if (atBottom) setShowLatest(false);
  }

  if (conversation === null) {
    return (
      <section className="chat-pane no-selection">
        <div className="no-selection-art"><Icon name="chat" /></div>
        <h2>选择一个会话开始聊天</h2>
        <p>实时消息会通过最近的 Cloudflare 边缘节点送达</p>
      </section>
    );
  }
  const members = description?.members ?? [];
  const online = connectionStatus === "online";
  const connectionLabel = connectionStatusLabel(connectionStatus);
  const subtitle = conversation.type === "p2p"
    ? connectionLabel
    : `${description?.memberCount ?? members.length} 位成员`;
  const outgoingPersisted = outgoing !== null && hasPersistedClientMessage(
    messages,
    outgoing.clientMsgId,
  );

  return (
    <section className={`chat-pane mobile-visible${online ? "" : " has-connection-alert"}`}>
      <header className="chat-header">
        <button className="mobile-back" type="button" aria-label="返回会话列表" onClick={onBack}><Icon name="back" /></button>
        <Avatar name={conversation.title} url={conversation.avatarUrl} size={38} />
        <div className="chat-heading"><h2>{conversation.title}</h2><span><i className={online ? "online" : "offline"} />{subtitle}</span></div>
        <button className="icon-button" type="button" onClick={() => { setShowDetails((value) => !value); }}
          aria-label="会话详情" aria-expanded={showDetails}><Icon name="more" /></button>
      </header>
      {online ? null : (
        <div className={`connection-alert ${connectionStatus}`} role="status" aria-live="polite">
          <span className="connection-alert-dot" />
          <span>{connectionLabel}，连接恢复后可继续发送消息</span>
        </div>
      )}
      <div className="message-scroll" ref={scrollRef} role="log" aria-live="polite"
        aria-busy={loading} aria-label={`${conversation.title}的聊天记录`}
        onScroll={trackScrollPosition}>
        {loading ? <div className="history-loading" role="status"><span className="spinner dark" /> 正在同步消息…</div> : null}
        {!loading && visibleMessages.length === 0 ? (
          <div className="conversation-start"><span><Icon name="chat" /></span><strong>会话已创建</strong>
            <p>发出第一条消息，开启对话</p></div>
        ) : visibleMessages.map((message, index) => (
          <Fragment key={message.messageId}>
            {shouldShowTimeDivider(message.createdAt, visibleMessages[index - 1]?.createdAt) ? (
              <div className="message-date"><time dateTime={new Date(message.createdAt).toISOString()}>
                {formatMessageTimeDivider(message.createdAt)}
              </time></div>
            ) : null}
            <MessageBubble message={message} currentUserId={account.userId}
              members={members} api={api} onEdit={onEdit} onRetract={onRetract} />
          </Fragment>
        ))}
        {outgoing === null || outgoingPersisted ? null : <OutgoingPreview activity={outgoing} account={account} />}
      </div>
      {showLatest ? <button className="jump-to-latest" type="button" onClick={() => {
        scrollToBottom("smooth");
        setShowLatest(false);
      }}>回到最新消息</button> : null}
      {conversation.type === "p2p" && systemAccount?.replyPolicy === "DISABLED" ? (
        <div className="system-account-reply-disabled" role="status">
          <Icon name="chat" /><span><strong>该会话不支持回复</strong><small>消息由系统账号 {systemAccount.displayName} 通过业务服务端发送</small></span>
        </div>
      ) : <Composer api={api} disabled={!online} onSend={onSend} onError={onError} onActivity={setOutgoing} />}
      {showDetails ? (
        <aside className="details-drawer" aria-label="会话详情">
          <header><div><p className="eyebrow">CONVERSATION</p><h3>会话详情</h3></div>
            <button type="button" className="icon-button" aria-label="关闭会话详情"
              onClick={() => { setShowDetails(false); }}><Icon name="close" /></button></header>
          <div className="details-hero"><Avatar name={conversation.title} url={conversation.avatarUrl} size={68} />
            <strong>{conversation.title}</strong><span>{typeLabel(conversation.type)} · {description?.memberCount ?? members.length} 人</span></div>
          <div className="detail-block"><label>会话 ID</label><code>{conversation.convId}</code></div>
          <div className="detail-block"><label>归属连接池</label><span>{description?.homePool ?? conversation.homePool}</span></div>
          <section className="conversation-preferences" aria-label="会话设置">
            <PreferenceButton label="置顶聊天" active={conversation.pinned}
              onClick={() => { onPreference(conversation, { pinned: !conversation.pinned }); }} />
            <PreferenceButton label="消息免打扰" active={conversation.muted}
              onClick={() => { onPreference(conversation, { muted: !conversation.muted }); }} />
            <button type="button" onClick={() => { onPreference(conversation, { markedUnread: true }); }}>
              <span>标为未读</span><small>在会话列表保留红点</small>
            </button>
            <button type="button" onClick={() => {
              if (window.confirm("清空本地可见的聊天记录？新消息仍会正常显示。")) {
                onPreference(conversation, { clearHistory: true });
              }
            }}><span>清空聊天记录</span><small>仅影响当前账号的可见历史</small></button>
            <button type="button" className="danger-text" onClick={() => {
              if (window.confirm("从会话列表中删除该聊天？收到新消息后会重新出现。")) {
                onPreference(conversation, { hidden: true });
              }
            }}><span>删除会话</span><small>不会删除服务端消息或其他成员数据</small></button>
          </section>
          <div className="member-list"><label>成员</label>{members.map((member) => (
            <div key={member.userId}><Avatar name={member.displayName} url={member.avatarUrl} size={36} />
              <span><strong>{member.displayName}{member.isCurrentUser ? "（我）" : ""}</strong>
                <small>{member.role === "owner" ? "群主" : member.role === "admin" ? "管理员" : "成员"}</small></span></div>
          ))}</div>
        </aside>
      ) : null}
    </section>
  );
}

function PreferenceButton({ label, active, onClick }: {
  readonly label: string;
  readonly active: boolean;
  readonly onClick: () => void;
}) {
  return <button type="button" role="switch" aria-checked={active} onClick={onClick}>
    <span>{label}</span><i className={active ? "preference-switch active" : "preference-switch"} aria-hidden="true" />
  </button>;
}

function OutgoingPreview({ activity, account }: {
  readonly activity: OutgoingActivity;
  readonly account: Account;
}) {
  const label = activity.phase === "preparing"
    ? "正在处理"
    : activity.phase === "uploading"
      ? `上传中 ${activity.progress}%`
      : activity.phase === "sending"
        ? "发送中"
        : "发送失败";
  return (
    <article className={`message-row self outgoing-row ${activity.phase}`} role="status" aria-live="polite">
      <div className="message-stack">
        {activity.contentType === "image" && activity.previewUrl !== undefined ? (
          <div className="outgoing-image">
            <img src={activity.previewUrl} alt="待发送图片" />
            <div className="upload-shade">
              <span className="progress-ring" style={{
                background: `conic-gradient(white ${activity.progress * 3.6}deg, rgba(255,255,255,.3) 0deg)`,
              }}><i>{activity.phase === "failed" ? "!" : activity.progress}</i></span>
            </div>
          </div>
        ) : (
          <div className="message-bubble outgoing-bubble">
            {activity.contentType === "audio" ? <span className="outgoing-audio">)))</span> : null}
            <p>{activity.previewText ?? "消息"}</p>
          </div>
        )}
        {activity.contentType !== "image" && activity.phase === "uploading" ? (
          <span className="outgoing-progress-track"><i style={{ width: `${activity.progress}%` }} /></span>
        ) : null}
        <div className="outgoing-status">
          {activity.phase === "failed" ? <span className="send-error-mark">!</span> : <span className="mini-spinner" />}
          <span>{label}</span>
        </div>
      </div>
      <Avatar name={account.displayName} url={account.avatarUrl} size={36} />
    </article>
  );
}

function connectionStatusLabel(status: ConnectionStatus): string {
  if (status === "online") return "在线";
  if (status === "connecting") return "正在连接";
  if (status === "reconnecting") return "正在重新连接";
  return "连接已断开";
}

function typeLabel(type: ConversationSummary["type"]): string {
  if (type === "p2p") return "私聊";
  if (type === "broadcast") return "广播";
  if (type === "channel") return "频道";
  if (type === "room") return "聊天室";
  return "群聊";
}
